import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { movimientos as apiMovimientos, abonos as apiAbonos, nombreFormaPago } from "../lib/api";
import { formatCurrency, formatHora, getTodayLocal } from "../lib/formato";
import VentaDetailModal from "../componentes/transacciones/VentaDetailModal";
import AbonoDetailModal from "../componentes/transacciones/AbonoDetailModal";
import { BackIcon, ForwardIcon } from "../componentes/Iconos";

// Formas de pago, en el orden en que se muestran al pie del día.
const FORMAS = [
  { clave: "efectivo", nombre: "Efectivo (en caja)", enCaja: true },
  { clave: "tarjeta", nombre: "Tarjeta" },
  { clave: "sinpe", nombre: "SINPE" },
  { clave: "transferencia", nombre: "Transferencia" },
];

const TOTALES_VACIOS = {
  contado: 0,
  credito: 0,
  ventas: 0,
  abonos: 0,
  ingresos: 0,
  por_forma_pago: { efectivo: 0, tarjeta: 0, sinpe: 0, transferencia: 0 },
  sin_forma_pago: 0,
};

// Suma o resta días a una fecha en formato YYYY-MM-DD, sin pasar por
// UTC: con new Date("2026-09-08") el navegador interpreta medianoche
// en Greenwich y en Costa Rica eso devuelve el día anterior.
const correrDia = (fecha, dias) => {
  const [anio, mes, dia] = fecha.split("-").map(Number);
  const d = new Date(anio, mes - 1, dia + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const tituloFecha = (fecha) => {
  const [anio, mes, dia] = fecha.split("-").map(Number);
  const cuando = new Date(anio, mes - 1, dia);

  // El día de hoy no necesita decir qué día de la semana es.
  if (fecha === getTodayLocal()) {
    const texto = new Intl.DateTimeFormat("es-CR", {
      day: "numeric",
      month: "long",
    }).format(cuando);
    return `Hoy, ${texto}`;
  }

  const texto = new Intl.DateTimeFormat("es-CR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(cuando);
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const MovimientosPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fecha = searchParams.get("fecha") || getTodayLocal();

  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState(TOTALES_VACIOS);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [ventaVista, setVentaVista] = useState(null);
  const [abonoVisto, setAbonoVisto] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const datos = await apiMovimientos.delDia(fecha);
      setItems(datos.items);
      setTotales(datos.totales);
    } catch (err) {
      setError(err.message);
      setItems([]);
      setTotales(TOTALES_VACIOS);
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const irA = (nuevaFecha) => setSearchParams({ fecha: nuevaFecha }, { replace: true });

  const verAbono = async (movimiento) => {
    try {
      const detalles = await apiAbonos.detalle(movimiento.id);
      setAbonoVisto({ detalles, cliente: movimiento.nombre_completo });
    } catch (err) {
      alert(err.message);
    }
  };

  const columna = (movimiento) => {
    if (movimiento.tipo === "abono") return "abono";
    return movimiento.tipo_pago === "contado" ? "contado" : "credito";
  };

  const NOMBRE_COLUMNA = { contado: "Contado", credito: "Crédito", abono: "Abono" };

  // Las celdas de las columnas que no corresponden se marcan como vacías
  // para poder esconderlas en el teléfono, donde cada movimiento se
  // dibuja como una tarjeta en vez de una fila de tres columnas.
  const celda = (movimiento, cual) => {
    if (columna(movimiento) !== cual) return <td className="num vacia" />;
    return (
      <td className="num">
        <span className={`monto-${cual}`}>{formatCurrency(movimiento.monto)}</span>
        <span className="tipo-en-fila">{NOMBRE_COLUMNA[cual]}</span>
      </td>
    );
  };

  const subtitulo = (movimiento) => {
    const partes = [];
    if (movimiento.detalle) partes.push(movimiento.detalle);
    const forma = nombreFormaPago(movimiento.metodo_pago);
    if (forma) partes.push(forma.toUpperCase());
    return partes.join(" · ");
  };

  return (
    <div className="movimientos-view">
      {ventaVista && (
        <VentaDetailModal
          ventaId={ventaVista.id}
          cliente={ventaVista.nombre_completo}
          onClose={() => setVentaVista(null)}
        />
      )}
      {abonoVisto && (
        <AbonoDetailModal
          details={abonoVisto.detalles}
          cliente={abonoVisto.cliente}
          onClose={() => setAbonoVisto(null)}
        />
      )}

      <div className="barra-dia">
        <div className="selector-dia">
          <button
            type="button"
            className="flecha-dia"
            onClick={() => irA(correrDia(fecha, -1))}
            title="Día anterior"
          >
            <BackIcon />
          </button>
          <h2 className="titulo-dia">{tituloFecha(fecha)}</h2>
          <button
            type="button"
            className="flecha-dia"
            onClick={() => irA(correrDia(fecha, 1))}
            title="Día siguiente"
          >
            <ForwardIcon />
          </button>
          <input
            type="date"
            className="fecha-directa"
            value={fecha}
            onChange={(e) => irA(e.target.value)}
            aria-label="Elegir otra fecha"
          />
        </div>
      </div>

      {cargando ? (
        <p>Cargando...</p>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : (
        <>
          <div className="tabla-dia-contenedor">
            <table className="tabla-dia">
              <thead>
                <tr>
                  <th className="col-marca" />
                  <th className="col-hora">Hora</th>
                  <th>Cliente</th>
                  <th className="num col-dinero">Contado</th>
                  <th className="num col-dinero">Crédito</th>
                  <th className="num col-dinero">Abono</th>
                </tr>
              </thead>
              <tbody>
                {items.map((movimiento) => (
                  <tr
                    key={`${movimiento.tipo}-${movimiento.id}`}
                    className="clickable-row"
                    onClick={() =>
                      movimiento.tipo === "venta"
                        ? setVentaVista(movimiento)
                        : verAbono(movimiento)
                    }
                  >
                    <td className="col-marca">
                      <span className={`franja ${columna(movimiento)}`} />
                    </td>
                    <td className="col-hora">{formatHora(movimiento.fecha)}</td>
                    <td>
                      <span className="nombre-cliente">{movimiento.nombre_completo}</span>
                      {subtitulo(movimiento) && (
                        <span className="subtitulo-movimiento">{subtitulo(movimiento)}</span>
                      )}
                    </td>
                    {celda(movimiento, "contado")}
                    {celda(movimiento, "credito")}
                    {celda(movimiento, "abono")}
                  </tr>
                ))}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="col-marca" />
                    <td colSpan="2" className="rotulo-totales">
                      Totales del día
                    </td>
                    <td className="num" data-etiqueta="Contado">
                      {formatCurrency(totales.contado)}
                    </td>
                    <td className="num" data-etiqueta="Crédito">
                      {formatCurrency(totales.credito)}
                    </td>
                    <td className="num" data-etiqueta="Abono">
                      {formatCurrency(totales.abonos)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {items.length === 0 && (
              <p className="empty-history">No hay movimientos registrados este día.</p>
            )}
          </div>

          {items.length > 0 && (
            <section className="resumen-dia">
              <div className="resumen-cifras">
                <div className="cifra-dia contado">
                  <span className="cifra-rotulo">Ventas de contado</span>
                  <strong className="cifra-monto">{formatCurrency(totales.contado)}</strong>
                </div>
                <div className="cifra-dia credito">
                  <span className="cifra-rotulo">Ventas a crédito</span>
                  <strong className="cifra-monto">{formatCurrency(totales.credito)}</strong>
                </div>
                <div className="cifra-dia total">
                  <span className="cifra-rotulo">Total vendido</span>
                  <strong className="cifra-monto">{formatCurrency(totales.ventas)}</strong>
                </div>
                <div className="cifra-dia abono">
                  <span className="cifra-rotulo">Abonos recibidos</span>
                  <strong className="cifra-monto">{formatCurrency(totales.abonos)}</strong>
                </div>
              </div>

              <div className="resumen-pagos">
                <span className="resumen-pagos-titulo">Cómo entró la plata</span>
                <div className="pagos-lista">
                  {FORMAS.map((forma) => (
                    <div
                      key={forma.clave}
                      className={`pago-item ${forma.enCaja ? "en-caja" : ""}`}
                    >
                      <span className="pago-rotulo">{forma.nombre}</span>
                      <strong className="pago-monto">
                        {formatCurrency(totales.por_forma_pago[forma.clave])}
                      </strong>
                    </div>
                  ))}
                  {totales.sin_forma_pago > 0 && (
                    <div className="pago-item sin-forma">
                      <span className="pago-rotulo">Sin especificar</span>
                      <strong className="pago-monto">
                        {formatCurrency(totales.sin_forma_pago)}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default MovimientosPage;
